import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import axios from 'axios';
import CompanySettingsDisplay, { PlaceholderLogo } from './CompanySettingsDisplay';
import '../../shared/StyledInput.css';
import { useErrorToast, useDismissError } from '../../shared/ErrorToastContext';
import RequiredMark from '../../shared/RequiredMark';
import { mediaFileUrl as logoUrl } from '../../shared/mediaFileUrl';
import FieldHelp from '../../shared/FieldHelp';
import { LockedPill } from '../../shared/Pill';
import { useAnchorPriceGroupName } from '../../shared/useAnchorPriceGroupName';
import SaveButton from '../../shared/SaveButton';
import CancelButton from '../../shared/CancelButton';
import { COUNTRIES, DEFAULT_COUNTRY } from '../../shared/countries';
import { addressParts, addressWarnings, companyInfoAddress, partsDroppedFor, capitaliseWords, COMPANY_INFO_FIELDS } from '../../shared/addressFormat';
import { RegionSelect, AddressFieldWarning, SuburbInput } from '../../shared/AddressFields';
import { DATE_FORMATS } from '../../shared/tenantClock';
import { NUMBER_FORMATS, CURRENCY_LABELS } from '../../shared/numberFormat';
import './CompanySettings.css';

const initialState = {
  company_name: '',
  abn: '',
  address_unit: '',
  address_street: '',
  country: DEFAULT_COUNTRY,
  address_district: '',
  address_city: '',
  address_state: '',
  address_postcode: '',
  phone_country_code: '+61',
  phone_number: '',
  phone_type: 'landline',
  email: '',
  website: '',
  about_us: '',
  instagram_url: '',
  currency_code: '',
  timezone: 'Australia/Sydney',
  date_format: 'dd/mm/yyyy',
  number_format: '1,234.56',
  currency_label: 'symbol',
  logo: null,
  site_icon: null,
  track_rrp: true,
};

// Grouped-field section treatment — matches ProductForm.js's sectionStyle/
// sectionHeaderStyle exactly, so this page's field groups read as the same
// pattern as the rest of the app instead of this page's former bespoke
// blue-fieldset styling.
// Every IANA zone the browser knows, labelled with its current UTC offset
// so "Australia/Brisbane (GMT+10)" vs "Australia/Sydney (GMT+11)" is
// visible at a glance during daylight saving. Built once per page load.
// Australia Post's Suburbs for an Australian Postcode, as it's typed (#158).
async function fetchPostcodeCheck(query) {
  const res = await axios.get('/api/postcode-check', { params: query });
  return res.data.result;
}

function buildTimeZoneOptions() {
  const zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : ['Australia/Sydney'];
  const now = new Date();
  return zones.map((zone) => {
    let offset = '';
    try {
      offset = new Intl.DateTimeFormat('en-AU', { timeZone: zone, timeZoneName: 'shortOffset' })
        .formatToParts(now).find((p) => p.type === 'timeZoneName')?.value || '';
    } catch {
      offset = '';
    }
    return { value: zone, label: offset ? `${zone.replace(/_/g, ' ')} (${offset})` : zone.replace(/_/g, ' ') };
  });
}

const sectionStyle = { padding: 16, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const sectionHeaderStyle = { margin: '0 0 10px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 };

// Confirm-and-lock popup (story #141) — Track RRP is a one-time, permanent
// choice made when Company Settings is first created, so unlike every other
// field on this form it can't just be Saved and revisited. Built from
// ReportOverlay.js's fixed/backdrop shape rather than reusing that component
// directly, since it needs Confirm/Cancel actions instead of a close-only
// report shell.
function TrackRrpConfirmDialog({ trackRrp, saving, onConfirm, onCancel }) {
  const anchorName = useAnchorPriceGroupName();
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{ width: '100%', maxWidth: 440, background: '#fff', borderRadius: 12, padding: 28, boxShadow: '0 8px 32px rgba(15,23,42,0.25)' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 600 }}>Confirm Track {anchorName}</h3>
        <p style={{ fontSize: 14, color: '#333', lineHeight: 1.5 }}>
          You're about to turn Track {anchorName} <strong>{trackRrp ? 'ON' : 'OFF'}</strong> for this business.
          {trackRrp
            ? ` ${anchorName} will be tracked for your products.`
            : ` ${anchorName} will not be tracked anywhere in the app.`}
        </p>
        <p style={{ fontSize: 14, color: '#333', lineHeight: 1.5 }}>
          This is a <strong>one-time, permanent choice</strong>. Once confirmed, it can't be changed later — for anyone, anywhere in the app.
        </p>
        <div style={{ marginTop: 20 }}>
          <SaveButton onClick={onConfirm} loading={saving}>{saving ? 'Saving...' : 'Confirm & Lock'}</SaveButton>
          <CancelButton onClick={onCancel} disabled={saving} style={{ marginLeft: 8 }} />
        </div>
      </div>
    </div>
  );
}

// Existing library images (category 'branding') the logo can be pointed at
// instead of re-uploading — same "reuse, don't re-upload" picker as Product
// Media's Attach Existing (ProductMediaPage.js), just a single-select since
// there's only ever one logo slot.
// pngOnly narrows the choices to PNGs, for the Site Icon.
function AttachExistingLogo({ excludeId, onAttach, pngOnly = false }) {
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    axios.get('/api/media-images', { params: { category: 'branding' } })
      .then((res) => setOptions((res.data.data || []).filter((img) => String(img.id) !== String(excludeId) && (!pngOnly || img.mime_type === 'image/png'))))
      .catch(() => setOptions([]));
  }, [excludeId, pngOnly]);

  if (!options.length) return null;

  const handleAttach = async () => {
    if (!selectedId) return;
    setAttaching(true);
    await onAttach(Number(selectedId));
    setSelectedId('');
    setAttaching(false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <select className="styled-input" value={selectedId} onChange={(e) => setSelectedId(e.target.value)} disabled={attaching} style={{ fontSize: 13, flex: '1 1 0', minWidth: 0 }}>
        <option value="">Attach existing...</option>
        {options.map((img) => (
          <option key={img.id} value={img.id}>{img.original_filename}</option>
        ))}
      </select>
      <button type="button" onClick={handleAttach} disabled={attaching || !selectedId} style={{ flexShrink: 0, background: 'none', border: 'none', color: '#1976d2', cursor: attaching || !selectedId ? 'not-allowed' : 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 }}>
        {attaching ? 'Attaching...' : 'Attach'}
      </button>
    </div>
  );
}

// One Branding image field: its label, a fixed-width preview box, and the
// controls beside it, so the Logo and Site Icon line up with each other.
const linkButtonStyle = (color, busy) => ({ background: 'none', border: 'none', color, cursor: busy ? 'not-allowed' : 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0 });

function BrandingImageField({ label, preview, uploadLabel, accept, saving, hasImage, onFileChange, onRemove, attach, hint }) {
  return (
    <div>
      <label style={{ fontSize: 13, fontWeight: 500 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginTop: 6 }}>
        <div style={{ width: 120, height: 56, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          {preview}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ ...linkButtonStyle('#1976d2', saving), textDecoration: 'none', fontWeight: 500 }}>
              {saving ? 'Saving...' : hasImage ? 'Replace' : uploadLabel}
              <input type="file" accept={accept} onChange={onFileChange} disabled={saving} style={{ display: 'none' }} />
            </label>
            {hasImage && (
              <button type="button" onClick={onRemove} disabled={saving} style={linkButtonStyle('#ff6f61', saving)}>Remove</button>
            )}
          </div>
          {attach}
          <div style={{ fontSize: 11, color: '#888' }}>{hint}</div>
        </div>
      </div>
    </div>
  );
}

// Uploading immediately replaces the live logo (independent of the main
// Save button, same "acts right away" pattern as ProductMediaPage's own
// Upload form) — the old MediaImage row stays in the Media Library
// unattached rather than being deleted, so it can still be picked again.
function LogoField({ logo, onUploaded, onRemoved }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [saving, setSaving] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post('/api/company-settings/logo', formData);
      onUploaded(res.data.data.logo);
      dismissError();
    } catch (err) {
      showError(err, 'Could not upload logo');
    }
    setSaving(false);
  };

  const handleAttachExisting = async (mediaImageId) => {
    try {
      const res = await axios.patch('/api/company-settings/logo', { media_image_id: mediaImageId });
      onUploaded(res.data.data.logo);
      dismissError();
    } catch (err) {
      showError(err, 'Could not attach logo');
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await axios.delete('/api/company-settings/logo');
      onRemoved();
      dismissError();
    } catch (err) {
      showError(err, 'Could not remove logo');
    }
    setSaving(false);
  };

  return (
    <BrandingImageField
      label="Logo"
      preview={logo
        ? <img src={logoUrl(logo.id)} alt="Company logo" style={{ maxWidth: 108, maxHeight: 44, objectFit: 'contain' }} />
        : <PlaceholderLogo width={108} height={44} />}
      uploadLabel="Upload logo"
      accept="image/png,image/svg+xml"
      saving={saving}
      hasImage={!!logo}
      onFileChange={handleFileChange}
      onRemove={handleRemove}
      attach={<AttachExistingLogo excludeId={logo?.id} onAttach={handleAttachExisting} />}
      hint="PNG or SVG, shown on printed Quotes"
    />
  );
}

// The Site Icon (favicon) in browser tabs and on phone home screens, for
// the website, the Customer portal and the back office. Same "acts right
// away" upload as LogoField, PNG only.
function SiteIconField({ icon, onUploaded, onRemoved }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [saving, setSaving] = useState(false);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post('/api/company-settings/site-icon', formData);
      onUploaded(res.data.data.site_icon);
      dismissError();
    } catch (err) {
      showError(err, 'Could not upload Site Icon');
    }
    setSaving(false);
  };

  const handleAttachExisting = async (mediaImageId) => {
    try {
      const res = await axios.patch('/api/company-settings/site-icon', { media_image_id: mediaImageId });
      onUploaded(res.data.data.site_icon);
      dismissError();
    } catch (err) {
      showError(err, 'Could not attach Site Icon');
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await axios.delete('/api/company-settings/site-icon');
      onRemoved();
      dismissError();
    } catch (err) {
      showError(err, 'Could not remove Site Icon');
    }
    setSaving(false);
  };

  return (
    <BrandingImageField
      label="Site Icon"
      preview={icon
        ? <img src={logoUrl(icon.id)} alt="Site Icon" style={{ width: 44, height: 44, objectFit: 'contain' }} />
        : <span style={{ fontSize: 11, color: '#9aa3ae' }}>None</span>}
      uploadLabel="Upload Site Icon"
      accept="image/png"
      saving={saving}
      hasImage={!!icon}
      onFileChange={handleFileChange}
      onRemove={handleRemove}
      attach={<AttachExistingLogo excludeId={icon?.id} onAttach={handleAttachExisting} pngOnly />}
      hint="Square PNG, 512 × 512, shown in browser tabs"
    />
  );
}

const PHONE_TYPES = [
  { value: 'landline', label: 'Landline' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'australia_wide', label: 'Australia Wide' },
  { value: 'freephone', label: 'Freephone' },
];


function CompanySettings({ onCompanyInfoChange }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const anchorName = useAnchorPriceGroupName();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [timeZoneOptions] = useState(buildTimeZoneOptions);
  const [showRrpConfirm, setShowRrpConfirm] = useState(false);
  const [rechecking, setRechecking] = useState(false);

  // No settings row saved yet — Company Settings is a singleton, so this is
  // the only signal the app has for "this save will create the row" versus
  // "this save is editing an existing one" (see updateCompanySettings).
  const isCreating = !form.id;

  // Phone mask by type
  const phonePlaceholders = {
    landline: '02 XXXX-YYYY (e.g. 02 9099-1678)',
    mobile: '04XXXXXXXX (e.g. 0412345678)',
    australia_wide: '1300XXXXXX (e.g. 1300123456)',
    freephone: '1800XXXXXX (e.g. 1800123456)',
  };

  // Format landline as '02 XXXX-YYYY' as user types
  function formatLandlineInput(value) {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 2) return digits;
    if (digits.length <= 6) return digits.slice(0,2) + ' ' + digits.slice(2);
    if (digits.length <= 10) return digits.slice(0,2) + ' ' + digits.slice(2,6) + '-' + digits.slice(6,10);
    return digits.slice(0,2) + ' ' + digits.slice(2,6) + '-' + digits.slice(6,10);
  }

  useEffect(() => {
    axios.get('/api/company-settings')
      .then(res => {
        let data = { ...initialState, ...res.data.data };
        // Format landline phone number for display in edit form
        if (data.phone_type === 'landline' && data.phone_number) {
          // Only format if not already formatted
          const digits = data.phone_number.replace(/\D/g, '');
          if (digits.length >= 6) {
            data.phone_number = formatLandlineInput(digits);
          }
        }
        setForm(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    // Load currencies
    axios.get('/api/currencies', { params: { limit: 100 } })
      .then(res => {
        const options = (res.data.data || []).map(c => ({
          value: c.currency_code,
          label: `${c.currency_code} - ${c.currency_name}`
        }));
        setCurrencyOptions(options);
      })
      .catch(() => {});
  }, [showEdit]);

  const handleChange = e => {
    const { name, value } = e.target;
    // Special formatting for landline phone
    if (name === 'phone_number' && form.phone_type === 'landline') {
      setForm(f => ({ ...f, [name]: formatLandlineInput(value) }));
    } else if (name === 'country') {
      // The parts both countries share carry over, and the ones the new
      // Country doesn't show are blanked (#157).
      const dropped = {};
      for (const key of Object.keys(partsDroppedFor(value))) dropped[COMPANY_INFO_FIELDS[key]] = '';
      setForm(f => ({ ...f, country: value, ...dropped }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
    setSuccess('');
  };

  const doSave = async (extra = {}) => {
    setSaving(true);
    setSuccess('');
    // Prepare data for backend: strip spaces/dashes from phone_number;
    // logo and Site Icon are managed by their own upload/remove endpoints,
    // not this form
    const { logo, site_icon, payment_options, ...submitForm } = form;
    if (submitForm.phone_type === 'landline' && submitForm.phone_number) {
      submitForm.phone_number = submitForm.phone_number.replace(/[^\d]/g, '');
    }
    try {
      const res = await axios.put('/api/company-settings', { ...submitForm, ...extra });
      // Australia Post's check, as the save left it (#158).
      setForm(f => ({ ...f, address_postcode_check: res.data.data?.address_postcode_check ?? null }));
      if (onCompanyInfoChange) {
        await onCompanyInfoChange();
      }
      setSuccess('Company information updated successfully.');
      setShowEdit(false);
      setShowRrpConfirm(false);
      dismissError();
    } catch (err) {
      showError(err, 'Update failed');
    }
    setSaving(false);
  };

  const handleSubmit = e => {
    e.preventDefault();
    // Track RRP (story #141) can't be skipped on first creation — every
    // Save attempt while there's no settings row yet routes through the
    // confirm-and-lock popup instead of saving directly.
    if (isCreating) {
      setShowRrpConfirm(true);
      return;
    }
    doSave();
  };

  const handleConfirmRrp = () => doSave({ track_rrp_confirmed: true });
  // An Australian Suburb, with the State (and Postcode, when searched by
  // name) that goes with a picked one (#158).
  const changeSuburb = fields => {
    setForm(f => ({ ...f, address_city: fields.suburb, ...(fields.state ? { address_state: fields.state } : {}), ...(fields.postcode ? { address_postcode: fields.postcode } : {}) }));
    setSuccess('');
  };

  if (loading) return <div>Loading...</div>;

  // Re-check: Australia Post's check of the address again (#158).
  const recheckAddress = async () => {
    setRechecking(true);
    try {
      const res = await axios.post('/api/company-settings/check-address');
      setForm(f => ({ ...f, address_postcode_check: res.data.data.address_postcode_check }));
      dismissError();
    } catch (err) {
      showError(err, 'Could not check the address');
    }
    setRechecking(false);
  };

  const handleEditOpen = () => {
    setShowEdit(true);
    setSuccess('');
  };
  const handleEditClose = () => {
    setShowEdit(false);
    setSuccess('');
  };

  if (!showEdit) {
    return <CompanySettingsDisplay data={form} onEdit={handleEditOpen} onRecheck={recheckAddress} rechecking={rechecking} />;
  }

  const addressWarningsNow = addressWarnings(companyInfoAddress(form));
  const localityParts = addressParts(form.country).filter(p => p.key !== 'line1' && p.key !== 'line2');

  return (
    <div style={{ width: '100%', maxWidth: 1020, boxSizing: 'border-box', margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>Edit Company Information</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0, marginBottom: 16 }}>Company details, address, and contact information used across quotes, reports, and invoices.</p>
      <form className="company-settings-form" onSubmit={handleSubmit} autoComplete="off">
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ ...sectionStyle, flex: 1, minWidth: 0 }}>
            <h4 style={sectionHeaderStyle}>Company Details</h4>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label>Company Name<RequiredMark /></label><br/>
                <input className="styled-input" name="company_name" value={form.company_name} onChange={handleChange} required style={{ width: '100%' }} />
              </div>
              <div style={{ width: 140 }}>
                <label>ABN<RequiredMark /></label><br/>
                <input className="styled-input" name="abn" value={form.abn} onChange={handleChange} required maxLength={11} style={{ width: '100%' }} />
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label>About Us</label><br/>
              <textarea
                className="styled-input"
                name="about_us"
                value={form.about_us}
                onChange={handleChange}
                rows={7}
                maxLength={600}
                placeholder="A short introduction to your company, printed at the top of your pricelists."
                style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ ...sectionStyle, flex: 1, minWidth: 0 }}>
            <h4 style={sectionHeaderStyle}>Branding</h4>
            <LogoField
              logo={form.logo}
              onUploaded={(logo) => setForm(f => ({ ...f, logo }))}
              onRemoved={() => setForm(f => ({ ...f, logo: null }))}
            />
            <div style={{ marginTop: 20 }}>
              <SiteIconField
                icon={form.site_icon}
                onUploaded={(site_icon) => setForm(f => ({ ...f, site_icon }))}
                onRemoved={() => setForm(f => ({ ...f, site_icon: null }))}
              />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
          <div style={{ ...sectionStyle, flex: 1 }}>
            <h4 style={sectionHeaderStyle}>Address</h4>
            <div style={{ marginBottom: 10 }}>
              <label>Country<RequiredMark /></label><br/>
              <select className="styled-input" name="country" value={form.country} onChange={handleChange} style={{ width: '100%', maxWidth: 220 }}>
                {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 14, marginBottom: 10 }}>
              <div style={{ width: '32%' }}>
                <label>Unit/Suite</label><br/>
                <input className="styled-input" name="address_unit" value={form.address_unit} onChange={handleChange} onBlur={() => setForm(f => ({ ...f, address_unit: capitaliseWords(f.address_unit) }))} style={{ width: '100%' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label>Street</label><br/>
                <input className="styled-input" name="address_street" value={form.address_street} onChange={handleChange} onBlur={() => setForm(f => ({ ...f, address_street: capitaliseWords(f.address_street) }))} style={{ width: '100%' }} />
                <AddressFieldWarning message={addressWarningsNow.line1} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {localityParts.map(part => (
              <div key={part.key} style={part.key === 'suburb' || part.key === 'district' ? { flex: 1, minWidth: 140 } : { width: '20%', minWidth: 110 }}>
                <label>{part.label}{part.required && <RequiredMark />}</label><br/>
                {part.options ? (
                  <RegionSelect country={form.country} value={form.address_state} options={part.options} onChange={v => { setForm(f => ({ ...f, address_state: v })); setSuccess(''); }} ariaLabel={part.label} />
                ) : part.key === 'suburb' && form.country === 'Australia' ? (
                  <SuburbInput
                    address={companyInfoAddress(form)}
                    fetchCheck={fetchPostcodeCheck}
                    onChange={changeSuburb}
                    inputProps={{ className: 'styled-input', name: 'address_city', 'aria-label': part.label, style: { width: '100%' } }}
                  />
                ) : (
                  <input className="styled-input" name={COMPANY_INFO_FIELDS[part.key]} value={form[COMPANY_INFO_FIELDS[part.key]] || ''} onChange={handleChange} onBlur={part.key === 'suburb' ? () => setForm(f => ({ ...f, address_city: capitaliseWords(f.address_city) })) : undefined} aria-label={part.label} style={{ width: '100%' }} />
                )}
                <AddressFieldWarning message={addressWarningsNow[part.key]} />
              </div>
              ))}
            </div>
          </div>

          <div style={{ ...sectionStyle, flex: 1 }}>
            <h4 style={sectionHeaderStyle}>Contact</h4>
            <div style={{ display: 'flex', gap: 14, marginBottom: 10 }}>
              <div style={{ width: 130, flexShrink: 0 }}>
                <label>Country Code</label><br/>
                <select className="styled-input" name="phone_country_code" value={form.phone_country_code} onChange={handleChange} style={{ width: '100%' }}>
                  <option value="+61">+61 (Australia)</option>
                </select>
              </div>
              <div style={{ width: 140, flexShrink: 0 }}>
                <label>Phone Type</label><br/>
                <select className="styled-input" name="phone_type" value={form.phone_type} onChange={handleChange} style={{ width: '100%' }}>
                  {PHONE_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label>Phone</label><br/>
                <input
                  className="styled-input"
                  name="phone_number"
                  value={form.phone_number}
                  onChange={handleChange}
                  placeholder={phonePlaceholders[form.phone_type] || 'Phone'}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 14 }}>
              <div style={{ flex: 1 }}>
                <label>Email</label><br/>
                <input className="styled-input" name="email" value={form.email} onChange={handleChange} placeholder="Email" style={{ width: '100%' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label>Website</label><br/>
                <input className="styled-input" name="website" value={form.website} onChange={handleChange} placeholder="Website" style={{ width: '100%' }} />
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label>Instagram</label><br/>
              <input className="styled-input" name="instagram_url" value={form.instagram_url} onChange={handleChange} placeholder="https://instagram.com/yourhandle" style={{ width: '100%' }} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
          <div style={{ ...sectionStyle, flex: 1 }}>
            <h4 style={sectionHeaderStyle}>Localisation</h4>
            <div style={{ marginBottom: 12 }}>
              <label>Default Currency<RequiredMark /></label><br/>
              <select className="styled-input" name="currency_code" value={form.currency_code} onChange={handleChange} style={{ width: '100%', maxWidth: 320 }}>
                <option value="">Select Currency</option>
                {currencyOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label htmlFor="currency_label">Currency Label<RequiredMark /></label><br/>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <select id="currency_label" className="styled-input" name="currency_label" value={form.currency_label} onChange={handleChange} style={{ width: '100%', maxWidth: 320 }}>
                  {CURRENCY_LABELS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <FieldHelp
                  fieldKey="foundation-company-settings.currency-label"
                  pageKey="company-info"
                  label="Currency Label"
                >
                  How an amount is labelled. Only the Default Currency can show as a bare symbol ($); an amount in any other currency is always marked (US$ or USD), so it can't be mistaken for the Default Currency.
                </FieldHelp>
              </div>
            </div>
            <div>
              <label htmlFor="timezone">Timezone<RequiredMark /></label><br/>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ flex: 1, maxWidth: 320 }}>
                  <Select
                    inputId="timezone"
                    aria-label="Timezone"
                    value={timeZoneOptions.find((opt) => opt.value === form.timezone) || null}
                    onChange={(opt) => setForm((f) => ({ ...f, timezone: opt ? opt.value : '' }))}
                    options={timeZoneOptions}
                    isSearchable
                    placeholder="Search timezone..."
                    noOptionsMessage={() => 'No timezones found'}
                    styles={{
                      container: base => ({ ...base, width: '100%' }),
                      // Same pinned chrome as the Suburb picker above, so it
                      // lines up with the native selects in this card.
                      control: base => ({
                        ...base,
                        minHeight: 34,
                        height: 34,
                        borderRadius: 4,
                        borderColor: '#a9a9a9',
                        boxShadow: 'none',
                        fontSize: 14,
                        '&:hover': { borderColor: '#a9a9a9' },
                      }),
                      valueContainer: base => ({ ...base, height: 32, padding: '0 8px' }),
                      input: base => ({ ...base, margin: 0, padding: 0 }),
                      indicatorsContainer: base => ({ ...base, height: 32 }),
                      indicatorSeparator: () => ({ display: 'none' }),
                      option: base => ({ ...base, fontSize: 14 }),
                      menuPortal: base => ({ ...base, zIndex: 9999 }),
                    }}
                    menuPortalTarget={document.body}
                  />
                </div>
                <FieldHelp
                  fieldKey="foundation-company-settings.timezone"
                  pageKey="company-info"
                  label="Timezone"
                >
                  The zone every business date is recorded in: today's date, sent-on dates, due dates and price effective dates. Dates and times on screen and in reports also show in this zone. Changing it applies to dates recorded from then on.
                </FieldHelp>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label htmlFor="date_format">Date Format<RequiredMark /></label><br/>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <select id="date_format" className="styled-input" name="date_format" value={form.date_format} onChange={handleChange} style={{ width: '100%', maxWidth: 320 }}>
                  {DATE_FORMATS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <FieldHelp
                  fieldKey="foundation-company-settings.date-format"
                  pageKey="company-info"
                  label="Date Format"
                >
                  How every date is shown and typed, for everyone: date fields, lists, detail pages, filters, reports, pricelist PDFs and emails.
                </FieldHelp>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label htmlFor="number_format">Number Format<RequiredMark /></label><br/>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <select id="number_format" className="styled-input" name="number_format" value={form.number_format} onChange={handleChange} style={{ width: '100%', maxWidth: 320 }}>
                  {NUMBER_FORMATS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </select>
                <FieldHelp
                  fieldKey="foundation-company-settings.number-format"
                  pageKey="company-info"
                  label="Number Format"
                >
                  The thousands and decimal separators for every number, shown or typed, for everyone: prices, costs, quantities, percentages, exchange rates and weights, on screen, in reports, pricelist PDFs and emails.
                </FieldHelp>
              </div>
            </div>
          </div>

          <div style={{ ...sectionStyle, flex: 1 }}>
            <h4 style={sectionHeaderStyle}>Pricing</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  name="track_rrp"
                  checked={!!form.track_rrp}
                  disabled={!isCreating}
                  onChange={e => setForm(f => ({ ...f, track_rrp: e.target.checked }))}
                />
                Track {anchorName}
              </label>
              <FieldHelp
                fieldKey="foundation-company-settings.track-rrp"
                pageKey="company-info"
                label={`Track ${anchorName}`}
              >
                A one-time, permanent choice about whether your business tracks {anchorName}. Confirmed once when Company Settings is first created — it can't be changed afterward.
              </FieldHelp>
              {!isCreating && <LockedPill title="This choice was confirmed and locked when Company Settings was first created — it can't be changed." />}
            </div>
          </div>
        </div>

        {success && <div style={{ color: '#2e7d32', marginTop: 16, fontSize: 14 }}>{success}</div>}

        <div style={{ marginTop: 20 }}>
          <SaveButton loading={saving} />
          <CancelButton onClick={handleEditClose} disabled={saving} style={{ marginLeft: 8 }} />
        </div>
      </form>
      {showRrpConfirm && (
        <TrackRrpConfirmDialog
          trackRrp={!!form.track_rrp}
          saving={saving}
          onConfirm={handleConfirmRrp}
          onCancel={() => setShowRrpConfirm(false)}
        />
      )}
    </div>
  );
}

export default CompanySettings;
